import { MapBrowserEvent } from "ol";
import { nearby, RerouteBase } from "./reroute";
import { Styles } from "./Styles";
import TrackVectorLayer from "./TrackVectorLayer";
import { TrackVectorLayerType } from "./TrackVectorLayerType";
import {firstBy} from "thenby";
import { getLength } from "ol/sphere";
import LineString from "ol/geom/LineString";
import _ from "lodash";
import Point from "ol/geom/Point";
import TrackPointFeature from "./TrackPointFeature";
import PreUndoEvent from "./PreUndoEvent";

export default class RerouteIndividual extends RerouteBase {
    
    protected handleMoveEvent(mapBrowserEvent: MapBrowserEvent<any>): void {
        const trackpointsNearby = mapBrowserEvent.map.getFeaturesAtPixel(mapBrowserEvent.pixel, {
            layerFilter: (l: any) => l instanceof TrackVectorLayer && l.type === TrackVectorLayerType.Trackpoints,
            hitTolerance: nearby
        })
        .filter((f: any) => f instanceof TrackPointFeature)
        .map((f: any) => f as TrackPointFeature)
       
        const sortedTrackpointsNearby = trackpointsNearby.sort(firstBy<TrackPointFeature>(v => -v.track.zIndex).thenBy(v => getLength(new LineString([v.getGeometry().getCoordinates(), mapBrowserEvent.coordinate]))))
        const newReroutingObject = _.first(sortedTrackpointsNearby) ?? null

        if(this.reroutingObject && this.reroutingObject !== newReroutingObject) {
            // if the current reroutingObject is not going to still be the reroutingObject, unhighlight it
            this.reroutingObject.setStyle(Styles.Trackpoint)
        }

        if(newReroutingObject) {
            newReroutingObject.setStyle(Styles.TrackpointHighlighted)
        }
        this.reroutingObject = newReroutingObject
    }

    setAnchors(): void {
        //sanity check
        if(this.reroutingObject === null) {
            throw new Error("setAnchors called when reroutingObject is null")
        }
        if(!(this.reroutingObject instanceof TrackPointFeature)) {
            throw new Error("setAnchors called when reroutingObject is of wrong type")
        }
        const trackLineCoords = this.reroutingObject.trackLineFeature.getGeometry().getCoordinates()
        if(this.reroutingObject.anchorIndex !== null) {
            // it's on an anchor, whether it's the first trackpoint in a segment, or the last
            if(this.reroutingObject.anchorIndex === 0) {
                // the first trackpoint of the first segment
                this.rerouteStartAnchor = null
                this.rerouteEndAnchor = trackLineCoords[this.reroutingObject.indexWithinFeature + 1] ?? null
            } else {
                // on an anchor that's not the first one (whether it's last trackpoint of previous segment, or first trackoint of next)
                this.rerouteStartAnchor = this.reroutingObject.track.getCoordinate(this.reroutingObject.anchorIndex - 1, -2) //we want the penultimate one (negative array index)
                this.rerouteEndAnchor = this.reroutingObject.track.getCoordinate(this.reroutingObject.anchorIndex, 1) // which should return null if anchorIndex is the last anchor
            }
        } else {
            // not on an anchor, -1 and +1 indexes should both refer to points within the segment.
            this.rerouteStartAnchor = trackLineCoords[this.reroutingObject.indexWithinFeature - 1] ?? null
            this.rerouteEndAnchor = trackLineCoords[this.reroutingObject.indexWithinFeature + 1] ?? null
        }
    }

    handleUpEventWhenRerouting(event: MapBrowserEvent<any>): boolean {
        if(this.reroutingObject !== null && this.reroutingObject instanceof TrackPointFeature) {
            const newCoords = event.coordinate.concat(this.reroutingObject.getGeometry().getCoordinates().slice(2)) //preserve the altitude
            this.dispatchEvent(new PreUndoEvent([this.reroutingObject.track.getRestoreData(this.options.renderer)], 'move trackpoint'))
            if(this.reroutingObject.anchorIndex !== null) {
                this.reroutingObject.track.setAnchorCoord(this.reroutingObject.anchorIndex, newCoords)
                // either first or last one in a segment - need to set the start/end of the neighbouring segment as well
                this.reroutingObject.track.setCoordinate(this.reroutingObject.anchorIndex, 0, newCoords)
                this.reroutingObject.track.setCoordinate(this.reroutingObject.anchorIndex - 1, -1, newCoords)
            } else {
                // only need to set the coord of the individual point
                this.reroutingObject.trackLineFeature.setCoordinate(this.reroutingObject.indexWithinFeature, newCoords)
            }
            this.stopRerouting()
            return true
        } else {
            console.warn('handleUpEventWhenRerouting: this.reroutingObject was null or wrong type: ', this.reroutingObject)
            return false
        }
    }

    handleDblClick(event: MapBrowserEvent<any>): boolean {
        const pointFeatures = event.map.getFeaturesAtPixel(event.pixel, {hitTolerance: nearby}).filter(p => p instanceof TrackPointFeature) as TrackPointFeature[] 
        const pointToRemove = _.last(_.sortBy(pointFeatures, p => p.track.zIndex))
        if (pointToRemove) { // _.last will return undefined if there aren't any
            pointToRemove.track.removeTrackpoint(pointToRemove, e => this.dispatchEvent(e))
            return false
        }
        return super.handleEvent(event)
    }

    handleDownEvent(event: MapBrowserEvent<any>): boolean {
        this.reroutingStartPixel = event.pixel
        return this.reroutingObject !== null
    }
}